/**
 * Originally based on the following JS library:
 * https://github.com/gre/bezier-easing
 * BezierEasing - use bezier curve for transition easing function
 * by Gaëtan Renaudeau 2014 - 2015 – MIT License
 *
 * cubic Bezier parameter demo - use the following site to help determine how parameters will effect X.
 * https://cubic-bezier.com/#.79,.04,.44,1.02
 *
 * Common easing functions
 * https://easings.net/en
 */
var BezierEasing = (function () {
    function BezierEasing(mX1, mY1, mX2, mY2) {
        // These values are established by empiricism with tests (tradeoff: performance VS precision)
        this.NEWTON_ITERATIONS = 4;
        this.NEWTON_MIN_SLOPE = 0.001;
        this.SUBDIVISION_PRECISION = 0.0000001;
        this.SUBDIVISION_MAX_ITERATIONS = 10;
        this.kSplineTableSize = 11;
        this.kSampleStepSize = 1.0 / (this.kSplineTableSize - 1.0);
        this.float32ArraySupported = typeof Float32Array === 'function';
        this.mX1 = mX1;
        this.mY1 = mY1;
        this.mX2 = mX2;
        this.mY2 = mY2;
        if (!(0 <= this.mX1 && this.mX1 <= 1 && 0 <= this.mX2 && this.mX2 <= 1)) {
            throw new Error('bezier x values must be in [0, 1] range');
        }
        this.linearEasing = this.mX1 === this.mY1 && this.mX2 === this.mY2 ? true : false;
        //if linearEasing, we can simply return
        if (this.linearEasing) {
            return;
        }
        // Precompute samples table
        this.sampleValues = this.float32ArraySupported ? new Float32Array(this.kSplineTableSize) : new Array(this.kSplineTableSize);
        for (var i = 0; i < this.kSplineTableSize; ++i) {
            this.sampleValues[i] = this.calcBezier(i * this.kSampleStepSize, mX1, mX2);
        }
    }
    BezierEasing.prototype.easing = function (x) {
        // Because JavaScript number are imprecise, we should guarantee the extremes are right.
        if (x === 0 || x === 1) {
            return x;
        }
        if (this.linearEasing) {
            return this.LinearEasing(x);
        }
        else {
            return this.calcBezier(this.getTForX(x), this.mY1, this.mY2);
        }
    };
    BezierEasing.prototype.A = function (aA1, aA2) {
        return 1.0 - 3.0 * aA2 + 3.0 * aA1;
    };
    BezierEasing.prototype.B = function (aA1, aA2) {
        return 3.0 * aA2 - 6.0 * aA1;
    };
    BezierEasing.prototype.C = function (aA1) {
        return 3.0 * aA1;
    };
    // Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
    BezierEasing.prototype.calcBezier = function (aT, aA1, aA2) {
        return ((this.A(aA1, aA2) * aT + this.B(aA1, aA2)) * aT + this.C(aA1)) * aT;
    };
    // Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
    BezierEasing.prototype.getSlope = function (aT, aA1, aA2) {
        return 3.0 * this.A(aA1, aA2) * aT * aT + 2.0 * this.B(aA1, aA2) * aT + this.C(aA1);
    };
    BezierEasing.prototype.binarySubdivide = function (aX, aA, aB, mX1, mX2) {
        var currentX;
        var currentT;
        var i = 0;
        do {
            currentT = aA + (aB - aA) / 2.0;
            currentX = this.calcBezier(currentT, mX1, mX2) - aX;
            if (currentX > 0.0) {
                aB = currentT;
            }
            else {
                aA = currentT;
            }
        } while (Math.abs(currentX) > this.SUBDIVISION_PRECISION && ++i < this.SUBDIVISION_MAX_ITERATIONS);
        return currentT;
    };
    BezierEasing.prototype.newtonRaphsonIterate = function (aX, aGuessT, mX1, mX2) {
        for (var i = 0; i < this.NEWTON_ITERATIONS; ++i) {
            var currentSlope = this.getSlope(aGuessT, mX1, mX2);
            if (currentSlope === 0.0) {
                return aGuessT;
            }
            var currentX = this.calcBezier(aGuessT, mX1, mX2) - aX;
            aGuessT -= currentX / currentSlope;
        }
        return aGuessT;
    };
    BezierEasing.prototype.LinearEasing = function (x) {
        return x;
    };
    BezierEasing.prototype.getTForX = function (aX) {
        var intervalStart = 0.0;
        var currentSample = 1;
        var lastSample = this.kSplineTableSize - 1;
        for (; currentSample !== lastSample && this.sampleValues[currentSample] <= aX; ++currentSample) {
            intervalStart += this.kSampleStepSize;
        }
        --currentSample;
        // Interpolate to provide an initial guess for t
        var dist = (aX - this.sampleValues[currentSample]) / (this.sampleValues[currentSample + 1] - this.sampleValues[currentSample]);
        var guessForT = intervalStart + dist * this.kSampleStepSize;
        var initialSlope = this.getSlope(guessForT, this.mX1, this.mX2);
        if (initialSlope >= this.NEWTON_MIN_SLOPE) {
            return this.newtonRaphsonIterate(aX, guessForT, this.mX1, this.mX2);
        }
        else if (initialSlope === 0.0) {
            return guessForT;
        }
        else {
            return this.binarySubdivide(aX, intervalStart, intervalStart + this.kSampleStepSize, this.mX1, this.mX2);
        }
    };
    return BezierEasing;
}());
export { BezierEasing };

//# sourceMappingURL=bezierEasing.js.map
